import { useTheme } from "next-themes";
import { CircleAlert, CircleCheck, Info, Loader2, TriangleAlert } from "lucide-react";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/** Lo que dura un aviso solo. */
const DURACION_MS = 5000;
/** Separación entre la salida de avisos que llegaron juntos: se van uno por uno, no en bloque. */
const ESCALON_MS = 1500;

let ultimaSalida = 0;

/**
 * Duración del aviso que se está creando. Varios que llegan de golpe tenían la misma cuenta y se
 * iban todos al mismo tiempo (también al quitar el mouse de encima, porque la pausa es para todos):
 * cada uno sale `ESCALON_MS` después del anterior. Uno solo dura `DURACION_MS`.
 */
function duracionEscalonada(): number {
  const ahora = Date.now();
  const duracion = Math.max(DURACION_MS, ultimaSalida - ahora + ESCALON_MS);
  ultimaSalida = ahora + duracion;
  return duracion;
}

type Opciones = { duration?: number } | undefined;
type Metodo = (mensaje: unknown, opciones?: Opciones) => string | number;

/**
 * Los avisos se crean con `toast` de "sonner" en todo el admin: se ajustan sus métodos una vez
 * aquí (el único Toaster) en vez de cambiar cada import. Si el aviso trae su propia duración
 * (p. ej. Infinity en uno de carga), se respeta.
 */
for (const nombre of ["success", "error", "warning", "info", "message"] as const) {
  const original = toast[nombre] as unknown as Metodo;
  (toast as unknown as Record<string, Metodo>)[nombre] = (mensaje, opciones) =>
    original(mensaje, opciones?.duration != null ? opciones : { ...opciones, duration: duracionEscalonada() });
}

/** Icono del aviso dentro de un círculo con el tono suave de su rol (como el SToast de la app). */
const icono = (Icon: typeof Info, tono: string, giro = false) => (
  <span className={`flex size-8 shrink-0 items-center justify-center rounded-full ${tono}`}>
    <Icon className={`size-[18px]${giro ? " animate-spin" : ""}`} aria-hidden />
  </span>
);

/**
 * Único toaster del admin (DESIGN §4), con el diseño del SToast de sozu-cliente-app: tarjeta con
 * borde y sombra, icono en círculo del tono, título en negrita y detalle en gris. Abajo a la
 * derecha, 5 s, con X, se quita deslizándolo y si llegan varios de golpe se apilan (hasta 5
 * visibles; al pasar el mouse se despliegan en lista y se pausa la cuenta) y se van uno por uno.
 *
 * El resultado de una acción va aquí; el error de un campo va pegado al campo y el de un modal,
 * dentro del modal.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="bottom-right"
      duration={DURACION_MS}
      visibleToasts={5}
      closeButton
      className="toaster group"
      icons={{
        success: icono(CircleCheck, "bg-success/15 text-success"),
        error: icono(CircleAlert, "bg-destructive/10 text-destructive"),
        warning: icono(TriangleAlert, "bg-warning/15 text-warning"),
        info: icono(Info, "bg-info/10 text-info"),
        loading: icono(Loader2, "bg-muted text-muted-foreground", true),
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast w-full max-w-[380px] items-start gap-3 rounded-xl border border-border bg-card p-4 pr-9 text-foreground shadow-lg",
          title: "text-[13px] font-semibold leading-snug text-foreground",
          description: "mt-0.5 text-[12px] leading-relaxed text-muted-foreground",
          icon: "m-0 size-8",
          closeButton:
            "!left-auto !right-2 !top-3 !translate-x-0 !translate-y-0 !border-0 !bg-transparent text-muted-foreground hover:!bg-muted hover:text-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
